import { Modal } from '@gettactic/components/src/overlay/modals/Modal/Modal';
import React from 'react';
import { FURNITURE_LIST, Furniture } from '../furniture-list';
import { DEFAULT_FURNITURE_FILL_COLOR } from './map/defines';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

interface FurnitureSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (furniture: Furniture) => void;
}

export const FurnitureSelectionModal: React.FC<
  FurnitureSelectionModalProps
> = ({ open, onClose, onSelect }) => {
  const [activeTab, setActiveTab] = React.useState('chairs');
  const [svgCache, setSvgCache] = React.useState<Record<string, string>>({});

  const furnitureCategories = React.useMemo(() => {
    const categories: Record<string, Furniture[]> = {};
    FURNITURE_LIST.forEach((item) => {
      if (!categories[item.category]) {
        categories[item.category] = [];
      }
      categories[item.category].push(item);
    });
    return categories;
  }, []);

  React.useEffect(() => {
    const fetchSvgs = async () => {
      const svgs: Record<string, string> = {};
      await Promise.all(
        FURNITURE_LIST.map(async (furniture) => {
          if (furniture.url.endsWith('.svg')) {
            try {
              const response = await fetch(furniture.url);
              svgs[furniture.url] = await response.text();
            } catch (error) {
              console.error(`Failed to load SVG: ${furniture.url}`, error);
            }
          }
        })
      );
      setSvgCache(svgs);
    };

    fetchSvgs();
  }, []);

  return (
    <Modal
      size={'max-w-[90%]'}
      opened={open}
      onClose={onClose}
      title={'Select furniture'}
    >
      <Tabs
        defaultValue="booths"
        className="w-full"
        value={activeTab}
        onValueChange={setActiveTab}
      >
        <TabsList className="grid w-full grid-cols-6">
          {Object.keys(furnitureCategories).map((category) => (
            <TabsTrigger
              key={category}
              value={category}
              className="flex items-center gap-2"
            >
              <img
                src={furnitureCategories[category][0].url}
                className="w-4 h-4"
                alt={`${category} icon`}
              />
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </TabsTrigger>
          ))}
        </TabsList>
        {Object.entries(furnitureCategories).map(([category, items]) => (
          <TabsContent key={category} value={category} className="mt-4 w-full">
            <div className="grid grid-cols-5 gap-4 p-4 max-h-[60vh] overflow-y-auto">
              {items.map((furniture) => (
                <button
                  key={furniture.id}
                  onClick={() => {
                    onSelect(furniture);
                    onClose();
                  }}
                  className="flex flex-col items-center gap-2 p-4 rounded-lg hover:bg-gray-100 transition-colors"
                  style={{ color: DEFAULT_FURNITURE_FILL_COLOR }}
                >
                  <span
                    className={
                      'flex items-center justify-center w-full h-[150px] overflow-hidden'
                    }
                  >
                    {svgCache[furniture.url] ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: svgCache[furniture.url]
                        }}
                      />
                    ) : (
                      <span className="w-24 h-24 bg-gray-200 animate-pulse" />
                    )}
                  </span>
                  <span className="text-sm text-gray-600 text-center">
                    {furniture.name}
                  </span>
                </button>
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </Modal>
  );
};
